import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../../../Context/ThemeContext';
import { RoleManagementHandler, RoleDetail } from './RoleManagementHandler';
import RoleFormModal from './RoleFormModal';

interface RolesResponse {
    roles?: RoleDetail[];
}

const formatDate = (dateStr: string | undefined): string => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
};

const RoleListPage: React.FC = () => {
    const { theme, isMobile } = useTheme();

    const [roles, setRoles] = useState<RoleDetail[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Modal state
    const [showModal, setShowModal] = useState(false);
    const [editingRole, setEditingRole] = useState<RoleDetail | null>(null);

    // Delete confirmation
    const [deleteConfirm, setDeleteConfirm] = useState<RoleDetail | null>(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    const fetchRoles = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const handler = new RoleManagementHandler();
            const response = await handler.getRoles();
            setRoles(response?.results || response?.roles || []);
        } catch (err) {
            console.error('Error fetching roles:', err);
            setError('Failed to load roles.');
            setRoles([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchRoles();
    }, [fetchRoles]);

    const handleAddRole = () => {
        setEditingRole(null);
        setShowModal(true);
    };

    const handleEditRole = (role: RoleDetail) => {
        setEditingRole(role);
        setShowModal(true);
    };

    const handleModalClose = () => {
        setShowModal(false);
        setEditingRole(null);
    };

    const handleModalSaved = () => {
        setShowModal(false);
        setEditingRole(null);
        fetchRoles();
    };

    const handleDeleteConfirm = (role: RoleDetail) => {
        setDeleteConfirm(role);
    };

    const handleDeleteExecute = async () => {
        if (!deleteConfirm?.id) return;
        setDeleteLoading(true);
        try {
            const handler = new RoleManagementHandler();
            await handler.deleteRole(deleteConfirm.id);
            setDeleteConfirm(null);
            fetchRoles();
        } catch (err: any) {
            console.error('Error deleting role:', err);
            setError(err?.response?.data?.message || err?.message || 'Failed to delete role.');
            setDeleteConfirm(null);
        } finally {
            setDeleteLoading(false);
        }
    };

    const styles: Record<string, React.CSSProperties> = {
        container: {
            padding: 0,
        },
        headerRow: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '24px',
            flexWrap: 'wrap' as const,
            gap: '12px',
        },
        titleArea: {},
        title: {
            fontSize: isMobile ? '22px' : '28px',
            fontWeight: 700,
            color: theme.titleColor,
            marginBottom: '4px',
        },
        subtitle: {
            fontSize: '14px',
            color: theme.textSecondary,
        },
        addButton: {
            padding: '10px 20px',
            fontSize: '14px',
            fontWeight: 600,
            color: '#fff',
            background: '#3b82f6',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            transition: 'background 0.2s',
        },
        tableContainer: {
            background: theme.card,
            borderRadius: '10px',
            overflow: 'hidden',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        },
        tableWrapper: {
            overflowX: 'auto' as const,
        },
        table: {
            width: '100%',
            borderCollapse: 'collapse' as const,
            tableLayout: 'fixed' as const,
            minWidth: isMobile ? '600px' : 'auto',
        },
        th: {
            padding: '14px 16px',
            textAlign: 'center' as const,
            fontWeight: 600,
            fontSize: '13px',
            color: theme.textSecondary,
            background: theme.tableHeader,
            borderBottom: `1px solid ${theme.border}`,
            userSelect: 'none' as const,
            whiteSpace: 'nowrap' as const,
        },
        td: {
            padding: '14px 16px',
            textAlign: 'center' as const,
            fontSize: '14px',
            color: theme.text,
            borderBottom: `1px solid ${theme.border}`,
        },
        actionButton: {
            padding: '5px 10px',
            fontSize: '12px',
            fontWeight: 600,
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            marginRight: '6px',
        },
        operationBadge: {
            display: 'inline-block',
            padding: '2px 8px',
            borderRadius: '10px',
            fontSize: '11px',
            fontWeight: 500,
            background: 'rgba(59, 130, 246, 0.15)',
            color: '#60a5fa',
            marginRight: '4px',
            marginBottom: '4px',
        },
        spinner: {
            width: '24px',
            height: '24px',
            border: '2px solid rgba(59, 130, 246, 0.3)',
            borderTop: '2px solid #3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
        },
        loadingContainer: {
            display: 'flex',
            flexDirection: 'column' as const,
            alignItems: 'center',
            justifyContent: 'center',
            padding: '60px 0',
            gap: '16px',
        },
        errorBanner: {
            padding: '12px 16px',
            borderRadius: '8px',
            background: 'rgba(239, 68, 68, 0.15)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            color: '#ef4444',
            fontSize: '14px',
            fontWeight: 500,
            marginBottom: '16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
        },
        emptyRow: {
            textAlign: 'center' as const,
            padding: '40px',
            color: theme.textSecondary,
            fontStyle: 'italic' as const,
        },
        overlay: {
            position: 'fixed' as const,
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
        },
        confirmModal: {
            background: theme.card,
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
        },
        confirmTitle: {
            fontSize: '18px',
            fontWeight: 700,
            color: theme.text,
            marginBottom: '12px',
        },
        confirmText: {
            fontSize: '14px',
            color: theme.textSecondary,
            marginBottom: '20px',
            lineHeight: 1.5,
        },
        confirmActions: {
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '12px',
        },
    };

    return (
        <div style={styles.container}>
            <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>

            <div style={styles.headerRow}>
                <div style={styles.titleArea}>
                    <h1 style={styles.title}>Role Management</h1>
                    <p style={styles.subtitle}>Create and manage administrator roles and permissions</p>
                </div>
                <button
                    style={styles.addButton}
                    onClick={handleAddRole}
                    onMouseEnter={(e) => { (e.target as HTMLElement).style.background = '#2563eb'; }}
                    onMouseLeave={(e) => { (e.target as HTMLElement).style.background = '#3b82f6'; }}
                >
                    + Add Role
                </button>
            </div>

            {error && (
                <div style={styles.errorBanner}>
                    <span>{error}</span>
                    <button
                        onClick={fetchRoles}
                        style={{ padding: '4px 12px', fontSize: '13px', fontWeight: 600, background: '#ef4444', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                    >
                        Retry
                    </button>
                </div>
            )}

            <div style={styles.tableContainer}>
                <div style={styles.tableWrapper}>
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={{ ...styles.th, width: '18%' }}>Name</th>
                                <th style={{ ...styles.th, width: '22%' }}>Description</th>
                                <th style={{ ...styles.th, width: '30%' }}>Operations</th>
                                <th style={{ ...styles.th, width: '12%' }}>Created</th>
                                <th style={{ ...styles.th, width: '18%' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={5} style={styles.td}>
                                        <div style={styles.loadingContainer}>
                                            <div style={styles.spinner} />
                                            <span style={{ color: theme.textSecondary }}>Loading roles...</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : roles.length === 0 ? (
                                <tr>
                                    <td colSpan={5} style={{ ...styles.td, ...styles.emptyRow }}>
                                        No roles found. Click &quot;Add Role&quot; to create one.
                                    </td>
                                </tr>
                            ) : (
                                roles.map((role) => (
                                    <tr
                                        key={role.id}
                                        onMouseEnter={(e) => { e.currentTarget.style.background = theme.tableRowHover; }}
                                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                                    >
                                        <td style={{ ...styles.td, fontWeight: 600 }}>{role.name || '-'}</td>
                                        <td style={{ ...styles.td, color: theme.textSecondary, fontSize: '13px' }}>
                                            {role.description || '-'}
                                        </td>
                                        <td style={styles.td}>
                                            {(role.permissionCount ?? 0) > 0 ? (
                                                <span style={styles.operationBadge}>
                                                    {role.permissionCount} permission{role.permissionCount !== 1 ? 's' : ''}
                                                </span>
                                            ) : (
                                                <span style={{ color: theme.textSecondary, fontSize: '12px', fontStyle: 'italic' }}>
                                                    No permissions
                                                </span>
                                            )}
                                        </td>
                                        <td style={styles.td}>{formatDate(role.creationDate)}</td>
                                        <td style={styles.td}>
                                            <button
                                                style={{ ...styles.actionButton, background: '#3b82f6', color: '#fff' }}
                                                onClick={() => handleEditRole(role)}
                                            >
                                                Edit
                                            </button>
                                            <button
                                                style={{ ...styles.actionButton, background: '#ef4444', color: '#fff' }}
                                                onClick={() => handleDeleteConfirm(role)}
                                            >
                                                Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Role Form Modal */}
            {showModal && (
                <RoleFormModal
                    role={editingRole}
                    onClose={handleModalClose}
                    onSaved={handleModalSaved}
                />
            )}

            {/* Delete Confirmation Modal */}
            {deleteConfirm && (
                <div style={styles.overlay} onClick={() => !deleteLoading && setDeleteConfirm(null)}>
                    <div style={styles.confirmModal} onClick={(e) => e.stopPropagation()}>
                        <div style={styles.confirmTitle}>Delete Role</div>
                        <div style={styles.confirmText}>
                            Are you sure you want to delete the role <strong>&quot;{deleteConfirm.name}&quot;</strong>?
                            This action cannot be undone. Any administrators assigned to this role will lose their permissions.
                        </div>
                        <div style={styles.confirmActions}>
                            <button
                                style={{
                                    padding: '8px 16px',
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    color: theme.text,
                                    background: 'transparent',
                                    border: `1px solid ${theme.border}`,
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                }}
                                onClick={() => setDeleteConfirm(null)}
                                disabled={deleteLoading}
                            >
                                Cancel
                            </button>
                            <button
                                style={{
                                    padding: '8px 16px',
                                    fontSize: '14px',
                                    fontWeight: 600,
                                    color: '#fff',
                                    background: '#ef4444',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    opacity: deleteLoading ? 0.6 : 1,
                                }}
                                onClick={handleDeleteExecute}
                                disabled={deleteLoading}
                            >
                                {deleteLoading ? 'Deleting...' : 'Delete Role'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RoleListPage;
